import classNames from 'classnames'
import React, {
  InputHTMLAttributes,
  PropsWithChildren,
  RefObject,
  useId,
  useMemo,
  useRef,
} from 'react'

import { Body1, Body2, Caption1 } from '@ui/Typography/Typography'

import { Box } from '@shared/components/Box'

import styles from './Radio.module.scss'

export type RadioSize = 'sm' | 'md' | 'lg'

function assignRef<T>(ref: React.Ref<T> | undefined, value: T) {
  if (!ref) return
  if (typeof ref === 'function') ref(value)
  else (ref as RefObject<T>).current = value
}

export interface RadioProps<T = string> extends PropsWithChildren {
  size?: RadioSize
  checked?: boolean
  defaultChecked?: boolean
  onChange?: (next: T) => void
  disabled?: boolean
  id?: string
  required?: boolean
  name?: string
  value?: T
  label?: string
  error?: string | boolean
  caption?: React.ReactNode
  autoFocus?: boolean
  inputRef?: React.Ref<HTMLInputElement>
}

export function Radio<T = string>({
  size = 'lg',
  checked,
  defaultChecked,
  onChange,
  disabled,
  required,
  id,
  name,
  label,
  value,
  caption,
  children,
  error,
  autoFocus,
  inputRef,
}: RadioProps<T>) {
  const internalRef = useRef<HTMLInputElement>(null)
  const setRef = (node: HTMLInputElement | null) => {
    internalRef.current = node
    assignRef(inputRef, node)
  }

  const rid = useId()
  const inputId = id ?? `rb-${rid}`
  const captionId = caption ? `${inputId}-caption` : undefined
  const errorId = error ? `${inputId}-error` : undefined
  const describedBy = useMemo(
    () => [captionId, errorId].filter(Boolean).join(' ') || undefined,
    [captionId, errorId]
  )

  const isControlled = typeof checked === 'boolean'
  const handleChange = () => onChange?.(value as T)

  const BodyText = () => {
    const textColor = disabled ? 'text-base-em' : 'text-high-em'
    const mainText = label ?? children
    if (!mainText) return null
    const text = required ? `${mainText} *` : mainText
    switch (size) {
      case 'sm':
        return <Caption1 color={textColor}>{text}</Caption1>
      case 'md':
        return <Body2 color={textColor}>{text}</Body2>
      case 'lg':
        return <Body1 color={textColor}>{text}</Body1>
    }
  }

  type DomValue = InputHTMLAttributes<HTMLInputElement>['value']
  const domValue = value as unknown as DomValue

  return (
    <label
      htmlFor={inputId}
      className={classNames(styles.root, styles[`size-${size}`], {
        [styles.disabled]: disabled,
        [styles.error]: !!error,
      })}
    >
      <input
        ref={setRef}
        id={inputId}
        required={required}
        name={name}
        value={domValue}
        type='radio'
        className={styles.input}
        {...(isControlled ? { checked } : defaultChecked !== undefined ? { defaultChecked } : {})}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        onChange={handleChange}
        autoFocus={autoFocus}
      />
      <span className={styles.box} aria-hidden data-checked={checked ? '' : undefined} />
      {(children || label || caption || error) && (
        <Box flex direction='column' gap='x2' pt={size === 'sm' ? 'x4' : undefined} pl='x8'>
          <BodyText />
          {caption && (
            <Caption1 id={captionId} color={disabled ? 'text-low-em' : 'text-med-em'}>
              {caption}
            </Caption1>
          )}
          {typeof error === 'string' && error.length > 0 && (
            <Caption1 id={errorId} color={disabled ? 'text-danger-low-em' : 'text-danger-high-em'}>
              {error}
            </Caption1>
          )}
        </Box>
      )}
    </label>
  )
}
